<template>
  <div>
    <div
      ref="messageContainer"
      class="message-container"
      :class="{
        'items-center': !notes.length,
        'justify-center': !notes.length,
      }"
      :style="{ marginBottom }"
    >
      <BaseMessage
        v-for="message in notes"
        :id="message.id"
        :key="message.id"
        :body="message.body"
        :attachments="message.attachments"
        :trigger-reason="message.trigger_reason"
        :date="message.updated_at"
        :user="message.user"
        :is-automated="message.auto_sent"
        :is-scheduled="message.is_draft"
        :direction="
          message.user.id === currentUser.id ? 'outgoing' : 'incoming'
        "
        type="note"
        @delete="handleDeleteNote"
        @edit="setNoteToEdit"
      ></BaseMessage>
      <div
        v-if="notes.length === 0"
        class="text-center"
      >
        <div class="font-semibold text-body-md">No notes saved yet</div>
        <div class="font-normal text-body-sm text-proxify-gray-600">
          Type in your first note about this applicant below
        </div>
      </div>
      <div
        v-else
        ref="divider"
        class="mt-4"
      ></div>
    </div>
    <div class="p-4">
      <AppFormTextEditor
        ref="editor"
        v-model="notesEditorOutput"
        name="NotesEditor"
        :on-send-message="handleSendNotes"
        :on-save-message="handleSaveNotes"
        placeholder="Type a new note"
        :options="quillOptions"
        :upload-progress="uploadProgress"
        :content-to-edit="noteToEdit"
        :on-cancel-edit="handleCancelEdit"
        :on-attachment-added="onAttachmentAdded"
        :on-attachment-removed="onAttachmentRemoved"
        :initial-value="notesEditorInitialValue"
        wrapper-classes="border border-proxify-gray-300 rounded-lg shadow-sm"
        submit-button-label="Save"
      />
    </div>
  </div>
</template>
<script>
import { mapState, useStore } from 'vuex';
import {
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
  defineAsyncComponent,
} from 'vue';
import Delta from 'quill-delta';
import api from '@/api';
import snackbarMessagesMixin from '@/mixins/snackbarMessagesMixin';
import { useDebounceFn } from '@vueuse/core';
import useDebounceFunctionStoppable from '@/composables/useDebounceFunctionStoppable';
import { useRouteParams } from '@vueuse/router';
import 'quill-mention';
import useScrollToItem from '@/composables/useScrollToItem';
import { useRoute, useRouter } from 'vue-router';
import BaseMessage from '@/components/Base/BaseMessage.vue';

export default {
  name: 'ApplicantNotes',
  components: {
    BaseMessage,
    AppFormTextEditor: defineAsyncComponent(
      () => import('@/components/App/AppFormTextEditor.vue')
    ),
  },
  mixins: [snackbarMessagesMixin],
  props: {
    notes: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    onReceiveNote: {
      type: Function,
      required: true,
    },
    onDeleteNote: {
      type: Function,
      required: false,
      default: () => {},
    },
    onEditNote: {
      type: Function,
      required: false,
      default: () => {},
    },
    applicant: {
      type: Object,
      required: false,
      default() {
        return {};
      },
    },
  },
  setup(props) {
    const messageContainer = ref(null);
    const notesEditorOutput = ref(null);
    const notesEditorInitialValue = ref(null);
    const marginBottom = ref('unset');
    const store = useStore();
    const applicationId = useRouteParams('id');
    const itemId = useRouteParams('itemId');
    const route = useRoute();
    const router = useRouter();

    const onAttachmentAdded = () => {
      marginBottom.value = '4.5rem';
    };
    const onAttachmentRemoved = () => {
      marginBottom.value = 'unset';
    };
    const { scrollToContainerBottom } = useScrollToItem(
      messageContainer,
      itemId,
      {
        scrollToBottom: true,
      },
      null,
      () =>
        router.push({
          params: {
            ...route.params,
            itemId: undefined,
          },
        })
    );
    const lastSavedDraftText = ref('');
    const storeDraftNote = () => {
      const ops = notesEditorOutput.value?.ops || [];
      const text = ops
        .map((op) => {
          if (typeof op.insert === 'string') return op.insert;
          // Preserve mentions as plain text (e.g., "@Name") in drafts
          if (op.insert && typeof op.insert === 'object' && op.insert.mention) {
            const { denotationChar = '@', value = '' } =
              op.insert.mention || {};
            return `${denotationChar}${value}`;
          }
          // Ignore other embeds in drafts
          return '';
        })
        .join('');
      // Only commit when text actually changed to avoid excessive writes
      if (text !== lastSavedDraftText.value) {
        store.commit('drafts/setNoteDraft', {
          id: route.params.id,
          note: text || '\n',
        });
        lastSavedDraftText.value = text;
      }
    };

    const {
      debounced: debounceStoreDraftNote,
      stop: stopDebounceStoreDraftNote,
    } = useDebounceFunctionStoppable(
      () => {
        storeDraftNote();
      },
      200,
      { maxWait: 5000, rejectOnCancel: false }
    );

    watch(
      () => props.notes,
      () => {
        scrollToContainerBottom?.();
      },
      {
        immediate: true,
        deep: true,
      }
    );
    watch(
      () => notesEditorOutput.value,
      () => {
        if (notesEditorOutput.value) {
          debounceStoreDraftNote();
        }
      }
    );

    onUnmounted(() => {
      // Prevent the next scheduled debounced draft save from running when component is destroyed
      try {
        stopDebounceStoreDraftNote?.();
      } catch (_) {
        // no-op
      }
    });
    watch(
      () => applicationId.value,
      () => {
        setNotesEditorInitialValue();
      }
    );

    const noteDrafts = computed(() => store.state.drafts.noteDrafts || []);

    const setNotesEditorInitialValue = () => {
      nextTick(() => {
        const entry = noteDrafts.value.find(
          (item) => String(item?.id) === String(applicationId.value)
        );
        let initialText = '';
        if (entry && typeof entry.note === 'string') {
          initialText = entry.note;
        } else if (entry && typeof entry.note !== 'string') {
          // Legacy/corrupted draft (non-string). Clean it up to prevent editor crashes.
          try {
            store.commit('drafts/deleteDraftNote', { id: applicationId.value });
          } catch (error) {
            store.commit('ui/addSnackbarMessage', {
              title: error.message || 'Error cleaning up legacy draft',
              type: 'error',
            });
          }
        }
        notesEditorInitialValue.value = new Delta([
          {
            insert: initialText,
          },
        ]);
      });
    };

    const quillOptions = computed(() => ({
      modules: {
        toolbar: [],
        mention: {
          allowedChars: /^[A-Za-z\s]*$/,
          mentionDenotationChars: ['@'],
          defaultMenuOrientation: 'bottom',
          offsetLeft: -16,
          onOpen: function () {
            document.querySelector('.activities-container').style.overflow =
              'unset';
          },
          onBeforeClose: function () {
            document.querySelector('.activities-container').style.overflow =
              null;
          },
          source: async function (searchTerm, renderList) {
            let values = [];
            const { users } = store.state.applicant;
            if (users.length) {
              values = users;
            } else {
              const { data: response } = await api.users.getAll();
              store.commit('applicant/setUsers', response.data);
              values = response.data;
            }
            renderList(
              values
                .filter(({ name, active }) => {
                  return (
                    name.toLowerCase().includes(searchTerm.toLowerCase()) &&
                    active
                  );
                })
                .map(({ id, name }) => ({ id, value: name })),
              searchTerm
            );
          },
        },
      },
      theme: 'snow',
    }));

    onMounted(() => {
      setNotesEditorInitialValue();
    });

    return {
      messageContainer,
      marginBottom,
      onAttachmentAdded,
      onAttachmentRemoved,
      quillOptions,
      notesEditorOutput,
      notesEditorInitialValue,
    };
  },
  data() {
    return {
      uploadProgress: 100,
      noteToEdit: null,
      notesChannelName: null,
    };
  },
  computed: {
    ...mapState({
      users: (state) => state.applicant.users,
      currentUser: (state) => state.auth.user,
    }),
  },
  watch: {
    '$route.params.id': function (newId, oldId) {
      if (newId !== oldId) {
        // Re-subscribe to the new channel when navigating between applicants
        this.listenNotesChannel();
      }
    },
  },
  mounted() {
    this.listenNotesChannel();
  },
  unmounted() {
    this.unsubscribeNotesChannel();
  },
  methods: {
    async handleSendNotes(data, { id }) {
      const { onUploadProgress } = this;
      const { data: note } = await api.applications.notes.post(data, {
        id,
        onUploadProgress,
      });
      this.$store.commit('drafts/deleteDraftNote', {
        id: this.$route.params.id,
      });
      this.onReceiveNote(note.response);
    },
    async handleDeleteNote(noteId) {
      const { data } = await api.applications.notes.delete({
        application_id: this.applicant.id,
        note_id: noteId,
      });
      this.onDeleteNote(noteId);
      this.showSuccessMessage(data.response);
    },
    setNoteToEdit(noteId) {
      this.noteToEdit = this.notes.find(({ id }) => id === noteId);
    },
    handleCancelEdit() {
      this.noteToEdit = null;
    },
    async handleSaveNotes(data, { note_id, application_id }) {
      const { onUploadProgress } = this;
      const { data: note } = await api.applications.notes.put(data, {
        note_id,
        application_id,
        onUploadProgress,
      });
      this.$store.commit('drafts/deleteDraftNote', {
        id: this.$route.params.id,
      });
      this.onEditNote(note.response);
    },
    onUploadProgress({ loaded, total }) {
      this.uploadProgress = Math.ceil((loaded / total) * 100);
    },
    listenNotesChannel() {
      const echo = window?.Echo;
      if (!echo) return;
      const { id } = this.$route.params;
      const channelName = `notes.${id}`;

      if (this.notesChannelName) {
        try {
          echo.leave(this.notesChannelName);
        } catch (error) {
          this.$store.commit('ui/addSnackbarMessage', {
            title: error.message || 'Error leaving notes channel',
            type: 'error',
          });
        }
      }

      this.notesChannelName = channelName;
      try {
        echo.channel(channelName).listen('NoteCreatedEvent', ({ note }) => {
          if (String(note?.user?.id) !== String(this.currentUser?.id)) {
            this.onReceiveNote(note);
          }
        });
      } catch (error) {
        this.$store.commit('ui/addSnackbarMessage', {
          title: error.message || 'Error listening to notes channel',
          type: 'error',
        });
        this.notesChannelName = null;
      }
    },
    unsubscribeNotesChannel() {
      const echo = window?.Echo;
      if (!echo) return;
      if (this.notesChannelName) {
        try {
          echo.leave(this.notesChannelName);
        } catch (error) {
          this.$store.commit('ui/addSnackbarMessage', {
            title: error.message || 'Error leaving notes channel',
            type: 'error',
          });
        }
        this.notesChannelName = null;
      }
    },
  },
};
</script>
<style scoped>
.message-container {
  @apply grow overflow-y-auto flex flex-col h-full p-4 gap-4;
}
</style>
